import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FileText, Trash2, UploadCloud, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API_URL = "/api";

interface DocumentUploadAdminProps {
  trackingNumber: string;
}

export const DocumentUploadAdmin = ({ trackingNumber }: DocumentUploadAdminProps) => {
  const [documents, setDocuments] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [docName, setDocName] = useState("");
  const [file, setFile] = useState<File | null>(null);

  useEffect(() => {
    fetchDocuments();
  }, [trackingNumber]);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/documents.php?tracking_number=${trackingNumber}`);
      const data = await response.json();
      if (data.success) {
        setDocuments(data.data);
      }
    } catch (error) {
      console.error("Failed to fetch documents:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file || !docName.trim()) {
      toast.error("Please provide a document name and select a file");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("tracking_number", trackingNumber);
    formData.append("document_name", docName.trim());
    formData.append("document", file);

    try {
      const response = await fetch(`${API_URL}/documents.php`, {
        method: "POST",
        body: formData,
      });
      const data = await response.json();
      
      if (data.success) {
        toast.success("Document uploaded successfully");
        setDocName("");
        setFile(null);
        // Reset file input
        const fileInput = document.getElementById("doc_file") as HTMLInputElement;
        if (fileInput) fileInput.value = "";
        fetchDocuments();
      } else {
        toast.error(data.error || "Failed to upload document");
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("An error occurred during upload");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Are you sure you want to delete this document?")) return;

    try {
      const response = await fetch(`${API_URL}/documents.php?id=${id}`, {
        method: "DELETE",
      });
      const data = await response.json();
      
      if (data.success) {
        toast.success("Document deleted");
        fetchDocuments();
      } else {
        toast.error(data.error || "Failed to delete document");
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("An error occurred during deletion");
    }
  };

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle>Shipment Documents</CardTitle>
      </CardHeader>
      <CardContent>
        {/* Upload Form */}
        <form onSubmit={handleUpload} className="bg-slate-50 p-4 rounded-lg border border-gray-200 mb-6 flex flex-col md:flex-row gap-4 items-end">
          <div className="flex-1 space-y-2 w-full">
            <Label htmlFor="doc_name">Document Name (e.g., Commercial Invoice)</Label>
            <Input 
              id="doc_name" 
              value={docName} 
              onChange={(e) => setDocName(e.target.value)} 
              placeholder="Enter document name"
              required
            />
          </div>
          <div className="flex-1 space-y-2 w-full">
            <Label htmlFor="doc_file">Select File (PDF, JPG, PNG)</Label>
            <Input 
              id="doc_file" 
              type="file" 
              onChange={(e) => setFile(e.target.files ? e.target.files[0] : null)}
              accept=".pdf,.jpg,.jpeg,.png"
              required
              className="cursor-pointer"
            />
          </div>
          <Button type="submit" disabled={uploading} className="w-full md:w-auto">
            {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UploadCloud className="mr-2 h-4 w-4" />}
            Upload
          </Button>
        </form>

        {/* Documents List */}
        <div>
          <h4 className="font-semibold mb-3 text-slate-700">Uploaded Documents</h4>
          {loading ? (
            <div className="flex justify-center p-4"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
          ) : documents.length === 0 ? (
            <p className="text-gray-500 text-sm italic p-4 text-center bg-gray-50 rounded border border-dashed">No documents uploaded yet.</p>
          ) : (
            <div className="space-y-3">
              {documents.map((doc) => (
                <div key={doc.id} className="flex items-center justify-between p-3 border rounded-md hover:bg-slate-50 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="bg-blue-100 p-2 rounded text-blue-700">
                      <FileText size={18} />
                    </div>
                    <div>
                      <p className="font-semibold text-sm">{doc.document_name}</p>
                      <p className="text-xs text-gray-500">{doc.file_name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => window.open(`/api${doc.file_path}`, '_blank')}
                    >
                      View
                    </Button>
                    <Button 
                      variant="destructive" 
                      size="sm"
                      onClick={() => handleDelete(doc.id)}
                    >
                      <Trash2 size={16} className="mr-1" /> Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
